import { api } from './api'
import { Client, PaginatedResponse } from '@/types'

export interface ClientFilters {
  status?: Client['status']
  tags?: string[]
  search?: string
  importBatch?: string
  page?: number
  limit?: number
}

export interface CreateClientRequest {
  email: string
  firstName?: string
  lastName?: string
  status?: Client['status']
  metadata?: Record<string, any>
  tags?: string[]
}

export interface UpdateClientRequest {
  email?: string
  firstName?: string
  lastName?: string
  status?: Client['status']
  metadata?: Record<string, any>
  tags?: string[]
}

export interface BulkUpdateRequest {
  clientIds: number[]
  updates: {
    status?: Client['status']
    tags?: string[]
    addTags?: string[]
    removeTags?: string[]
  }
}

export interface ImportCsvRequest {
  file: File
  options: {
    skipDuplicates?: boolean
    updateExisting?: boolean
    defaultStatus?: Client['status']
    tags?: string[]
  }
}

export interface ImportResult {
  success: number
  failed: number
  skipped: number
  duplicates: number
  importBatch: string
  errors?: Array<{ row: number; email: string; error: string }>
}

export const clientService = {
  async fetchClients(filters: ClientFilters = {}): Promise<PaginatedResponse<Client>> {
    // Backend returns: { success: true, data: Client[], pagination: {...} }
    const response = await api.get<{
      success: boolean
      data: Client[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/clients', {
      params: filters,
    })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
    }
  },

  async getClient(id: number): Promise<Client> {
    // Backend returns: { success: true, data: Client }
    const response = await api.get<{ success: boolean; data: Client }>(`/clients/${id}`)
    return response.data.data
  },

  async createClient(data: CreateClientRequest): Promise<Client> {
    // Backend returns: { success: true, data: Client }
    const response = await api.post<{ success: boolean; data: Client }>('/clients', data)
    return response.data.data
  },

  async updateClient(id: number, data: UpdateClientRequest): Promise<Client> {
    // Backend returns: { success: true, data: Client }
    const response = await api.put<{ success: boolean; data: Client }>(`/clients/${id}`, data)
    return response.data.data
  },

  async deleteClient(id: number): Promise<void> {
    await api.delete(`/clients/${id}`)
  },

  async bulkDelete(clientIds: number[]): Promise<void> {
    await api.post('/clients/bulk-delete', { clientIds })
  },

  async bulkUpdate(data: BulkUpdateRequest): Promise<void> {
    await api.post('/clients/bulk-update', data)
  },

  async importCsv(data: ImportCsvRequest): Promise<ImportResult> {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('options', JSON.stringify(data.options))

    const response = await api.post<ImportResult>('/clients/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async exportCsv(filters: ClientFilters = {}): Promise<Blob> {
    const response = await api.get('/clients/export-csv', {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },

  async getAllTags(): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: string[] }>('/clients/tags')
    return response.data.data
  },

  async getStats(): Promise<{
    total: number
    active: number
    unsubscribed: number
    bounced: number
    blocked: number
    byTag: Record<string, number>
  }> {
    // Backend may return: { success: true, data: {...} }
    const response = await api.get<{ success: boolean; data: any }>('/clients/stats')
    return response.data.data || response.data
  },
}
